import { DevelopStrengthProgressApiModel } from "../../api-models";
import { DevelopPlan } from "../../services/cms/develop-plan";
import CmsService from "../../services/cms/cms-service";
import {
  CmsDevelopPageRelationship,
  DayOrPageType,
  globalPlanStructure,
} from "../../services/cms/cms-type-definitions";

export interface PlanStepModel {
  id: string;
  uid: string;
  title: string;
  subtitle: string;
  isAvailable: boolean;
  isCurrent: boolean;
  isComplete: boolean;
  isLibrary: boolean;
  dayOrPageType: string;
}

export interface PlanWeekModel {
  id: string;
  title: string;
  steps: PlanStepModel[];
  isLibrary: boolean;
}

export function getDayNumber(day: DayOrPageType): number {
  const dayNumber = +day;
  if (Number.isNaN(dayNumber)) {
    throw new Error(`Unexpected plan day number: ${day}`);
  }

  return dayNumber;
}

export function getStepRoleFromDayNumber(day: DayOrPageType): string {
  const dayNumber = getDayNumber(day);
  return `day_${dayNumber}`;
}

function isStepAvailable(
  day: DayOrPageType | undefined,
  progress: DevelopStrengthProgressApiModel,
) {
  if (!day) {
    return false;
  }
  const dayNumber = getDayNumber(day);
  if (dayNumber <= (progress.lastCompletedDayIndex ?? 0) + 1) {
    return true;
  }

  return false;
}

function isStepCurrent(day: DayOrPageType | undefined, progress: DevelopStrengthProgressApiModel) {
  if (!day) {
    return false;
  }
  const dayNumber = getDayNumber(day);
  return dayNumber === (progress.lastCompletedDayIndex ?? 0) + 1;
}

function isStepComplete(day: DayOrPageType | undefined, progress: DevelopStrengthProgressApiModel) {
  if (!day) {
    return false;
  }
  const dayNumber = getDayNumber(day);
  return (
    typeof progress.lastCompletedDayIndex === "number" &&
    progress.lastCompletedDayIndex >= dayNumber
  );
}

export function getPlanWeeks(
  cms: CmsService,
  plan: DevelopPlan,
  progress: DevelopStrengthProgressApiModel,
): PlanWeekModel[] {
  // // We get the currentStep to ensure that we are always updated if the current step changes
  // // (even if the logic below was short-cut on a previous call)
  // const currentStep = this.currentStep;

  // if (!this.cmsLibrary || !this.doc) {
  //   return null;
  // }

  // debug("Calculating planWeeks model", { planStructure: globalPlanStructure });

  // We get the availability hash so this property is updated, e.g. when the day changes
  // const availabilityHash = this.availabilityHash;

  const stepToPlanStepModel = (step: CmsDevelopPageRelationship, stepTestId: string) =>
    ({
      id: step.id,
      uid: step.uid,
      testId: stepTestId,
      isAvailable: isStepAvailable(step.data!.day_or_page_type, progress),
      isCurrent: isStepCurrent(step.data!.day_or_page_type, progress),
      isComplete: isStepComplete(step.data!.day_or_page_type, progress),
      title: cms.asText(step.data!.title!),
      subtitle: cms.asText(step.data!.subtitle!),
      dayOrPageType: step.data!.day_or_page_type,
      isLibrary: false,
    } as PlanStepModel);

  const weeks = globalPlanStructure.map((x) => {
    const week: PlanWeekModel = {
      id: `week-${x.weekNumber}`,
      steps: x.days.map((day) =>
        stepToPlanStepModel(
          plan.cmsPlan[day.cmsDayMember],
          `week-${x.weekNumber}-step-${day.dayNumber}`,
        ),
      ),
      title: cms.asText(plan.cmsPlan[x.cmsTitleMember]),
      isLibrary: false,
    };
    return week;
  });

  return weeks;
}
